import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { trackLogin } from "../../lib/analytics";
import AuthFormHeader from "./AuthFormHeader";
import {
  authButtonClass,
  authFooterClass,
  authInputClass,
  authLinkClass,
} from "./authStyles";

const LoginForm = ({ onClose, switchToSignup }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const { signIn, resendVerificationEmail } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const toastOptions = { duration: 1000 };

    try {
      setNeedsVerification(false);
      await signIn(email, password);
      trackLogin("password");
      toast.success("Welcome back!", toastOptions);
      onClose();
    } catch (error) {
      const message = error.message?.toLowerCase() ?? "";
      if (message.includes("email not confirmed")) {
        setNeedsVerification(true);
        toast.error(
          "Please verify your email before signing in. Check your inbox and spam folder for the verification link.",
          { ...toastOptions, duration: 5000 }
        );
      } else {
        toast.error(error.message, toastOptions);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error("Enter your email address first.");
      return;
    }

    setResending(true);
    try {
      await resendVerificationEmail(email);
      toast.success(
        "Verification email sent. Check your inbox and spam folder.",
        { duration: 5000 }
      );
    } catch (error) {
      const message = error.message?.toLowerCase() ?? "";
      if (message.includes("rate limit")) {
        toast.error(
          "Too many emails sent. Wait a few minutes and check spam for an earlier message.",
          { duration: 5000 }
        );
      } else {
        toast.error(error.message, { duration: 5000 });
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full px-6 py-8 sm:px-9 sm:py-10">
      <AuthFormHeader
        title="Welcome Back"
        subtitle="Sign in to your JmoLux collection"
      />
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={authInputClass}
          required
        />
        <input
          type="password"
          name="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={authInputClass}
          required
        />
        <button type="submit" disabled={loading} className={`${authButtonClass} mt-2`}>
          {loading ? "Logging in..." : "Login"}
        </button>
        {needsVerification && (
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resending}
            className="w-full text-sm text-amber-600 hover:text-amber-500 underline underline-offset-2 disabled:opacity-50"
          >
            {resending ? "Sending..." : "Resend verification email"}
          </button>
        )}
      </form>
      <p className={authFooterClass}>
        Don&apos;t have an account?{" "}
        <button type="button" onClick={switchToSignup} className={authLinkClass}>
          Sign up
        </button>
      </p>
    </div>
  );
};

export default LoginForm;
